import { Component, Element, h, Host, Listen } from "@stencil/core";
import { scrollToTarget } from "../../../utils/scrollToTarget";
import { elementsSelectors } from "../supported-elements-selectors";

/**
 * @slot (default) - insert the content of the various form steps, each wrapped
 * in a ux-form-step element
 */

@Component({
    tag: "ux-form-steps"
})
export class MultipleStepForm {
    private baseClass = "ux-form-steps";
    private tabContainer: HTMLUxTabContainerElement;
    private uxFormElement: HTMLUxFormElement;

    @Element() element!: HTMLUxFormStepsElement;

    private nextStepButton = "data-ux-form-steps-next";
    private previousStepButton = "data-ux-form-steps-prev";

    connectedCallback() {
        const closestForm = this.element.closest("ux-form");
        if (closestForm) {
            this.uxFormElement = closestForm as HTMLUxFormElement;
        }
    }

    @Listen("click", { target: "window" })
    async handleButtonClicks(e: Event) {
        const eTarget = e.target as HTMLElement;
        const activeTab = this.element.querySelector("ux-tab.is-active") as HTMLUxTabElement;
        if (!this.element.contains(eTarget)) {
            return;
        }
        if (eTarget.hasAttribute(this.nextStepButton)) {
            await this.validateFormStep(activeTab);
            setTimeout(async () => {
                const isActiveStepValid = this.isFormStepValid(activeTab);
                if (isActiveStepValid) {
                    await this.goToNextStep(activeTab);
                    scrollToTarget({
                        target: this.element,
                        additionalOffsetTop: 30
                    });
                }
            }, 100);
        } else if (eTarget.hasAttribute(this.previousStepButton)) {
            await this.goToPreviousStep(activeTab);
            scrollToTarget({
                target: this.element,
                additionalOffsetTop: 30
            });
        }
    }

    private goToNextStep = async (activeTab: HTMLUxTabElement) => {
        const activeTabStep = activeTab.closest("ux-form-step");
        const nextTabStep = activeTabStep?.nextElementSibling?.querySelector("ux-tab");
        if (!nextTabStep) {
            return;
        }
        const nextTab = nextTabStep.getAttribute("tab-id");
        if (nextTab) {
            await this.tabContainer.setActiveTab(nextTab);
        }
    };

    private goToPreviousStep = async (activeTab: HTMLUxTabElement) => {
        const activeTabStep = activeTab.closest("ux-form-step");
        const prevTabStep = activeTabStep?.previousElementSibling?.querySelector("ux-tab");
        if (!prevTabStep) {
            return;
        }
        const prevTab = prevTabStep.getAttribute("tab-id");
        if (prevTab) {
            await this.tabContainer.setActiveTab(prevTab);
        }
    };

    private validateFormStep = async (activeStep: HTMLUxTabElement) => {
        await this.uxFormElement.validateElements(activeStep);
    };

    private isFormStepValid = (activeStep: HTMLUxTabElement): boolean => {
        const formElements: HTMLUxInputFieldElement[] = Array.from(activeStep.querySelectorAll(elementsSelectors.join(", ")));
        let isValid = true;
        formElements.forEach((formElement: HTMLUxInputFieldElement) => {
            if (formElement.error) {
                isValid = false;
            }
        });
        return isValid;
    };

    render() {
        return (
            <Host class={this.baseClass}>
                <ux-tab-container ref={(el) => (this.tabContainer = el as HTMLUxTabContainerElement)} no-navigation>
                    <slot />
                </ux-tab-container>
            </Host>
        );
    }
}
